import { Add, DeleteOutline, Edit, Visibility } from '@mui/icons-material'
import { com } from '@eidu/web'
import EntityWithLabelAndRelated from '../../../domain/entity/EntityWithLabelAndRelated'
import { findEntityTypeByFieldId } from '../../../util/EntityTypeExtensions'
import { fieldValueToString } from '../../../domain/entity/DraftFieldValue'
import { groupEntityTypesByKind } from '../../../util/entityKindGroup'
import getEntityPathLabel from '../../../pages/permission/path/EntityPathUtils'
import actionLabel from '../../../domain/permission/actionLabel'
import { PermissionPolicyDTO } from '../../../domain/permission/PermissionPolicyDTO'
import { formatSelectorHeaderLabel, formatSelectorLabelText, type SelectorHeaderLabel } from '../SelectorHeaderLabel'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import EntityId = com.eidu.sharedlib.entity.EntityId
import FieldId = com.eidu.sharedlib.entity.field.FieldId
import EntitySelector = com.eidu.sharedlib.entity.selector.EntitySelector
import EntitySelectorPermission = com.eidu.sharedlib.entity.permission.EntitySelectorPermission
import EntityPathPermission = com.eidu.sharedlib.entity.permission.EntityPathPermission
import EntityAction = com.eidu.sharedlib.entity.permission.EntityAction
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction
import PermissionGrant = com.eidu.sharedlib.entity.permission.PermissionGrant
import Action = com.eidu.sharedlib.entity.permission.Action
import Permission = com.eidu.sharedlib.entity.permission.Permission

export type ActionScope = { all: boolean; limited: boolean }
export type ActionAccessInfo = { total: number; all: number; limited: number }
export type SelectorOption = { typeId: string | null; scope: ActionScope }

export type RowHeader =
  | { id: string; type: 'global'; label: string; action: Action }
  | { id: string; type: 'kind'; label: string }
  | { id: string; type: 'entity'; label: string; entityTypeId: EntityTypeId }

export type ColumnHeader = {
  id: string
  parentKey: string | null
  selector: EntitySelector
  label: SelectorHeaderLabel
  permissions: Permission[]
  entityActionAccessByTypeId: Map<string, Map<EntityAction, ActionAccessInfo>>
  entityActionAccessSummary: Map<EntityAction, ActionAccessInfo>
  permissionsByTypeAndAction: Map<string, Map<EntityAction, Permission[]>>
  permissionsByAction: Map<EntityAction, Permission[]>
  policyNamesByAction: Map<Action, Set<string>>
  policyNamesByTypeAndAction: Map<string, Map<EntityAction, Set<string>>>
}

export const boldCheckSx = {
  stroke: 'currentColor',
  strokeWidth: 1.5,
} as const

export const CRUD_ACTIONS: { action: EntityAction; Icon: typeof Add }[] = [
  { action: EntityAction.CreateEntity, Icon: Add },
  { action: EntityAction.ReadEntity, Icon: Visibility },
  { action: EntityAction.UpdateEntity, Icon: Edit },
  { action: EntityAction.DeleteEntity, Icon: DeleteOutline },
]

export const isCrudEntityAction = (action: EntityAction) =>
  action === EntityAction.CreateEntity ||
  action === EntityAction.ReadEntity ||
  action === EntityAction.UpdateEntity ||
  action === EntityAction.DeleteEntity

export const collectSelectorOptions = (
  selector: EntitySelector,
  entityTypes: readonly EntityType[]
): SelectorOption[] => {
  switch (true) {
    case EntitySelector.All.equals(selector):
      return [{ typeId: null, scope: { all: true, limited: false } }]
    case EntitySelector.None.equals(selector):
      return []
    case selector instanceof EntitySelector.OfType:
      return [{ typeId: selector.typeId.toString(), scope: { all: true, limited: false } }]
    case selector instanceof EntitySelector.Single:
      return [{ typeId: selector.typeId.toString(), scope: { all: false, limited: true } }]
    case selector instanceof EntitySelector.FieldValueEquals:
    case selector instanceof EntitySelector.FieldValueEqualsUserEntityId:
    case selector instanceof EntitySelector.FieldValueEqualsUserFieldValue: {
      const entityType = findEntityTypeByFieldId(entityTypes, selector.fieldId)
      return entityType ? [{ typeId: entityType.id.toString(), scope: { all: false, limited: true } }] : []
    }
    case selector instanceof EntitySelector.And:
    case selector instanceof EntitySelector.Or:
      return [...selector.selectors.asJsReadonlyArrayView()].flatMap((item) =>
        collectSelectorOptions(item, entityTypes)
      )
    case selector instanceof EntitySelector.Not:
      return [{ typeId: null, scope: { all: false, limited: true } }]
    default:
      return []
  }
}

export const collectSelectorFieldIds = (selector: EntitySelector): FieldId[] => {
  if (selector instanceof EntitySelector.FieldValueEquals) return [selector.fieldId]
  if (selector instanceof EntitySelector.FieldValueEqualsUserEntityId) return [selector.fieldId]
  if (selector instanceof EntitySelector.FieldValueEqualsUserFieldValue) return [selector.fieldId, selector.userFieldId]
  if (selector instanceof EntitySelector.And)
    return [...selector.selectors.asJsReadonlyArrayView()].flatMap(collectSelectorFieldIds)
  if (selector instanceof EntitySelector.Or)
    return [...selector.selectors.asJsReadonlyArrayView()].flatMap(collectSelectorFieldIds)
  if (selector instanceof EntitySelector.Not) return collectSelectorFieldIds(selector.selector)
  return []
}

const normalizeSortValue = (value: string) => value.trim().toLowerCase()

export const selectorHeaderSortKey = (label: SelectorHeaderLabel) => {
  const primary = normalizeSortValue(label.primary)
  let fieldName = ''
  let fieldValue = ''
  if (label.secondary) {
    const separatorIndex = label.secondary.indexOf(':')
    if (separatorIndex >= 0) {
      fieldName = label.secondary.slice(0, separatorIndex).trim()
      fieldValue = label.secondary.slice(separatorIndex + 1).trim()
    } else {
      fieldValue = label.secondary.trim()
    }
  }
  return {
    primary,
    fieldName: normalizeSortValue(fieldName),
    fieldValue: normalizeSortValue(fieldValue),
  }
}

export const selectorKey = (selector: EntitySelector, entityTypes: readonly EntityType[]): string => {
  switch (true) {
    case EntitySelector.All.equals(selector):
      return 'all'
    case selector instanceof EntitySelector.OfType:
      return `type:${selector.typeId.toString()}`
    case selector instanceof EntitySelector.Single:
      return `single:${selector.typeId.toString()}:${selector.entityId.toString()}`
    case selector instanceof EntitySelector.FieldValueEquals: {
      const entityType = findEntityTypeByFieldId(entityTypes, selector.fieldId)
      const typeKey = entityType ? entityType.id.toString() : 'unknown'
      const valueKey = fieldValueToString(selector.value)
      return `field:${typeKey}:${selector.fieldId.toString()}:${valueKey}`
    }
    case selector instanceof EntitySelector.FieldValueEqualsUserEntityId:
      return `field-user-entity:${selector.fieldId.toString()}`
    case selector instanceof EntitySelector.FieldValueEqualsUserFieldValue:
      return `field-user-field:${selector.fieldId.toString()}:${selector.userFieldId.toString()}`
    case selector instanceof EntitySelector.And:
      return `and:${[...selector.selectors.asJsReadonlyArrayView()]
        .map((item) => selectorKey(item, entityTypes))
        .join('|')}`
    case selector instanceof EntitySelector.Or:
      return `or:${[...selector.selectors.asJsReadonlyArrayView()]
        .map((item) => selectorKey(item, entityTypes))
        .join('|')}`
    case selector instanceof EntitySelector.Not:
      return `not:${selectorKey(selector.selector, entityTypes)}`
    default:
      return selector.toString()
  }
}

export const parentColumnKey = (selector: EntitySelector, entityTypes: readonly EntityType[]): string | null => {
  switch (true) {
    case EntitySelector.All.equals(selector):
      return null
    case selector instanceof EntitySelector.OfType:
      return 'all'
    case selector instanceof EntitySelector.Single:
      return `type:${selector.typeId.toString()}`
    case selector instanceof EntitySelector.FieldValueEquals:
    case selector instanceof EntitySelector.FieldValueEqualsUserEntityId:
    case selector instanceof EntitySelector.FieldValueEqualsUserFieldValue: {
      const owningType = findEntityTypeByFieldId(entityTypes, selector.fieldId)
      return owningType ? `type:${owningType.id.toString()}` : null
    }
    default:
      return null
  }
}

export const formatPermissionTarget = (
  permission: Permission,
  entityTypes: readonly EntityType[],
  entitiesByTypeId: ReadonlyMap<EntityTypeId, ReadonlyMap<EntityId, EntityWithLabelAndRelated>>
): string => {
  if (permission instanceof EntitySelectorPermission)
    return formatSelectorLabelText(permission.targets, entityTypes, entitiesByTypeId)
  if (permission instanceof EntityPathPermission) return getEntityPathLabel(entityTypes, permission.path)
  return ''
}

export const findEffectiveAccess = (
  columnByKey: ReadonlyMap<string, ColumnHeader>,
  column: ColumnHeader,
  action: EntityAction,
  typeId?: string
): { access: ActionAccessInfo; inherited: boolean; sourceColumn: ColumnHeader } | null => {
  let current: ColumnHeader | undefined = column
  let inherited = false
  while (current) {
    const access = typeId
      ? current.entityActionAccessByTypeId.get(typeId)?.get(action)
      : current.entityActionAccessSummary.get(action)
    if (access && access.total > 0) return { access, inherited, sourceColumn: current }
    inherited = true
    current = current.parentKey ? columnByKey.get(current.parentKey) : undefined
  }
  return null
}

export const findEffectivePolicyNames = (
  columnByKey: ReadonlyMap<string, ColumnHeader>,
  column: ColumnHeader,
  action: EntityAction,
  typeId?: string
): string[] => {
  let current: ColumnHeader | undefined = column
  const names = new Set<string>()
  while (current) {
    const set = typeId
      ? current.policyNamesByTypeAndAction.get(typeId)?.get(action)
      : current.policyNamesByAction.get(action)
    set?.forEach((name) => names.add(name))
    current = current.parentKey ? columnByKey.get(current.parentKey) : undefined
  }
  return Array.from(names).sort((a, b) => a.localeCompare(b))
}

export const findEffectiveGlobalPolicyNames = (
  columnByKey: ReadonlyMap<string, ColumnHeader>,
  column: ColumnHeader,
  action: Action
): string[] => {
  let current: ColumnHeader | undefined = column
  const names = new Set<string>()
  while (current) {
    current.policyNamesByAction.get(action)?.forEach((name) => names.add(name))
    current = current.parentKey ? columnByKey.get(current.parentKey) : undefined
  }
  return Array.from(names).sort((a, b) => a.localeCompare(b))
}

export const findEffectiveGlobalCheck = (
  columnByKey: ReadonlyMap<string, ColumnHeader>,
  column: ColumnHeader,
  action: Action
): { inherited: boolean } | null => {
  let current: ColumnHeader | undefined = column
  let inherited = false
  while (current) {
    if (current.permissions.some((permission) => permission.action === action)) return { inherited }
    inherited = true
    current = current.parentKey ? columnByKey.get(current.parentKey) : undefined
  }
  return null
}

type SourcedPermission = { permission: Permission; policyName: string | null }

const expandGrantPermissions = (
  grant: { permissionGrant: PermissionGrant },
  policiesById: ReadonlyMap<string, PermissionPolicyDTO>
): SourcedPermission[] => {
  const out: SourcedPermission[] = []
  for (const permission of grant.permissionGrant.permissions.asJsReadonlySetView())
    out.push({ permission, policyName: null })
  for (const policyId of grant.permissionGrant.policyIds.asJsReadonlySetView()) {
    const policy = policiesById.get(policyId.toString())
    if (!policy) continue
    for (const permission of policy.policy.permissions.asJsReadonlySetView())
      out.push({ permission, policyName: policy.policy.name })
  }
  return out
}

export const buildColumnHeaders = (
  permissionGrants: ReadonlyArray<{ permissionGrant: PermissionGrant }>,
  entityTypes: readonly EntityType[],
  entitiesByTypeId: ReadonlyMap<EntityTypeId, ReadonlyMap<EntityId, EntityWithLabelAndRelated>>,
  policiesById: ReadonlyMap<string, PermissionPolicyDTO> = new Map()
): ColumnHeader[] => {
  const grouped = new Map<
    string,
    { selector: EntitySelector; label: SelectorHeaderLabel; sourcedPermissions: SourcedPermission[] }
  >()
  permissionGrants.forEach((grant) => {
    const selector = grant.permissionGrant.userSelector
    const key = selectorKey(selector, entityTypes)
    const label = formatSelectorHeaderLabel(selector, entityTypes, entitiesByTypeId)
    const entry = grouped.get(key) ?? { selector, label, sourcedPermissions: [] }
    entry.sourcedPermissions.push(...expandGrantPermissions(grant, policiesById))
    grouped.set(key, entry)
  })
  // Make sure every user-kind entity type appears as a column even if it has no grants —
  // empty columns are a useful tell that the role hasn't been assigned anything yet.
  entityTypes
    .filter((t) => t.kind.equals(EntityTypeKind.Companion.User))
    .forEach((userType) => {
      const ofTypeSelector = new EntitySelector.OfType(userType.id)
      const key = selectorKey(ofTypeSelector, entityTypes)
      if (!grouped.has(key)) {
        grouped.set(key, {
          selector: ofTypeSelector,
          label: formatSelectorHeaderLabel(ofTypeSelector, entityTypes, entitiesByTypeId),
          sourcedPermissions: [],
        })
      }
    })
  return Array.from(grouped.entries())
    .sort(([, a], [, b]) => {
      const left = selectorHeaderSortKey(a.label)
      const right = selectorHeaderSortKey(b.label)
      const primary = left.primary.localeCompare(right.primary, undefined, { sensitivity: 'base' })
      if (primary !== 0) return primary
      const fieldName = left.fieldName.localeCompare(right.fieldName, undefined, { sensitivity: 'base' })
      if (fieldName !== 0) return fieldName
      return left.fieldValue.localeCompare(right.fieldValue, undefined, { sensitivity: 'base' })
    })
    .map(([key, value]) => buildColumnHeaderEntry(key, value, entityTypes))
}

const buildColumnHeaderEntry = (
  key: string,
  value: { selector: EntitySelector; label: SelectorHeaderLabel; sourcedPermissions: SourcedPermission[] },
  entityTypes: readonly EntityType[]
): ColumnHeader => {
  const entityActionAccessByTypeId = new Map<string, Map<EntityAction, ActionAccessInfo>>()
  const entityActionAccessSummary = new Map<EntityAction, ActionAccessInfo>()
  const permissionsByTypeAndAction = new Map<string, Map<EntityAction, Permission[]>>()
  const permissionsByAction = new Map<EntityAction, Permission[]>()
  const policyNamesByAction = new Map<Action, Set<string>>()
  const policyNamesByTypeAndAction = new Map<string, Map<EntityAction, Set<string>>>()
  const addPolicyNameForAction = (action: Action, policyName: string) => {
    const set = policyNamesByAction.get(action) ?? new Set<string>()
    set.add(policyName)
    policyNamesByAction.set(action, set)
  }
  const addPolicyNameForTypeAction = (typeId: string, action: EntityAction, policyName: string) => {
    const actionMap = policyNamesByTypeAndAction.get(typeId) ?? new Map<EntityAction, Set<string>>()
    const set = actionMap.get(action) ?? new Set<string>()
    set.add(policyName)
    actionMap.set(action, set)
    policyNamesByTypeAndAction.set(typeId, actionMap)
  }
  const addAccessForTypeId = (typeId: string, action: EntityAction, scope: ActionScope) => {
    const actionMap = entityActionAccessByTypeId.get(typeId) ?? new Map<EntityAction, ActionAccessInfo>()
    const existing = actionMap.get(action) ?? { total: 0, all: 0, limited: 0 }
    actionMap.set(action, {
      total: existing.total + 1,
      all: existing.all + (scope.all ? 1 : 0),
      limited: existing.limited + (scope.limited ? 1 : 0),
    })
    entityActionAccessByTypeId.set(typeId, actionMap)
  }
  const addAccessSummary = (action: EntityAction, scope: ActionScope) => {
    const existing = entityActionAccessSummary.get(action) ?? { total: 0, all: 0, limited: 0 }
    entityActionAccessSummary.set(action, {
      total: existing.total + 1,
      all: existing.all + (scope.all ? 1 : 0),
      limited: existing.limited + (scope.limited ? 1 : 0),
    })
  }
  const addPermissionForTypeAndAction = (typeId: string, action: EntityAction, permission: Permission) => {
    const actionMap = permissionsByTypeAndAction.get(typeId) ?? new Map<EntityAction, Permission[]>()
    const arr = actionMap.get(action) ?? []
    arr.push(permission)
    actionMap.set(action, arr)
    permissionsByTypeAndAction.set(typeId, actionMap)
  }
  const addPermissionForAction = (action: EntityAction, permission: Permission) => {
    const arr = permissionsByAction.get(action) ?? []
    arr.push(permission)
    permissionsByAction.set(action, arr)
  }
  const applySelectorOption = (
    option: SelectorOption,
    action: EntityAction,
    permission: Permission,
    policyName: string | null
  ) => {
    addAccessSummary(action, option.scope)
    addPermissionForAction(action, permission)
    if (policyName !== null) addPolicyNameForAction(action, policyName)
    if (!isCrudEntityAction(action)) return
    if (option.typeId === null) {
      entityTypes.forEach((type) => {
        addAccessForTypeId(type.id.toString(), action, option.scope)
        addPermissionForTypeAndAction(type.id.toString(), action, permission)
        if (policyName !== null) addPolicyNameForTypeAction(type.id.toString(), action, policyName)
      })
      return
    }
    addAccessForTypeId(option.typeId, action, option.scope)
    addPermissionForTypeAndAction(option.typeId, action, permission)
    if (policyName !== null) addPolicyNameForTypeAction(option.typeId, action, policyName)
  }

  value.sourcedPermissions.forEach(({ permission, policyName }) => {
    if (permission instanceof EntitySelectorPermission) {
      const options = collectSelectorOptions(permission.targets, entityTypes)
      options.forEach((option) => applySelectorOption(option, permission.action, permission, policyName))
    } else if (permission instanceof EntityPathPermission) {
      applySelectorOption(
        { typeId: permission.path.toType.toString(), scope: { all: false, limited: true } },
        permission.action,
        permission,
        policyName
      )
    } else if (policyName !== null) {
      addPolicyNameForAction(permission.action, policyName)
    }
  })

  return {
    id: key,
    parentKey: parentColumnKey(value.selector, entityTypes),
    selector: value.selector,
    label: value.label,
    permissions: value.sourcedPermissions.map((s) => s.permission),
    entityActionAccessByTypeId,
    entityActionAccessSummary,
    permissionsByTypeAndAction,
    permissionsByAction,
    policyNamesByAction,
    policyNamesByTypeAndAction,
  }
}

export const buildRowHeaders = (entityTypes: readonly EntityType[]): RowHeader[] => {
  const globalActions: Action[] = [
    ...GlobalAction.values(),
    EntityAction.SubmitForm,
    EntityAction.ViewFormSubmissions,
    EntityAction.ShareSchoolLogin,
  ]

  const rows: RowHeader[] = globalActions.map((action) => ({
    id: `global-${action}`,
    type: 'global',
    label: actionLabel(action),
    action,
  }))

  const groups = groupEntityTypesByKind(entityTypes, (t) => t)
  for (const group of groups) {
    rows.push({ id: `kind-${group.kindKey}`, type: 'kind', label: group.label })
    const sortedTypes = group.entries
      .slice()
      .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' }))
    for (const type of sortedTypes) {
      rows.push({
        id: `type-${type.id.toString()}`,
        type: 'entity',
        label: String(type.name),
        entityTypeId: type.id,
      })
    }
  }

  return rows
}
